
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getDriversByBusOwner,
  getConductorsByBusOwner,
  assignDriverToBus,
  assignConductorToBus,
  unassignDriverFromBus,
  unassignConductorFromBus,
  deleteDriver,
  deleteConductor
} from '../../Services/Operations/ProductAPI';
import '../../Styles/BusManagementModal.css';
import EditBusModal from './EditBusModal';
import AddDriverModal from './AddDriverModal';
import AddConductorModal from './AddConductorModal';

const BusManagementModal = ({ owner, buses, onClose, onUpdate }) => {
  const { token } = useSelector(state => state.user);
  const [loading, setLoading] = useState(false);
  const [selectedBus, setSelectedBus] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [searchDriver, setSearchDriver] = useState('');
  const [searchConductor, setSearchConductor] = useState('');
  const [activeTab, setActiveTab] = useState('buses');
  const [actionLoading, setActionLoading] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [stats, setStats] = useState({
    totalBuses: 0,
    activeBuses: 0,
    totalDrivers: 0,
    assignedDrivers: 0,
    totalConductors: 0,
    assignedConductors: 0
  });

  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [showAddConductorModal, setShowAddConductorModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBusToEdit, setSelectedBusToEdit] = useState(null);
const getTicketTypeBadge = (bus) => {
  if (!bus.ticketBookingType) {
    return { label: 'Not Set', color: '#6c757d', icon: '❓' };
  }
  
  const { instantTicket, seatBooking } = bus.ticketBookingType;
  
  if (instantTicket && seatBooking) {
    return { label: 'Both', color: '#8b5cf6', icon: '🔄' };
  } else if (instantTicket) {
    return { label: 'Instant', color: '#22c55e', icon: '⚡' };
  } else if (seatBooking) {
    return { label: 'Seat', color: '#f59e0b', icon: '💺' };
  }
  
  return { label: 'None', color: '#ef4444', icon: '❌' };
};

  const handleAddDriverSuccess = (newDriver) => {
    setDrivers(prev => [newDriver, ...prev]);
    setStats(prev => ({
      ...prev,
      totalDrivers: prev.totalDrivers + 1
    }));
  };

  const handleAddConductorSuccess = (newConductor) => {
    setConductors(prev => [newConductor, ...prev]);
    setStats(prev => ({
      ...prev,
      totalConductors: prev.totalConductors + 1
    }));
  };

  const handleEditBus = (bus, e) => {
    if (e) e.stopPropagation();
    const busWithLocalRoute = {
      ...bus,
      localRouteName: bus.localRouteName || null
    };
    console.log("Passing bus to EditBusModal:", busWithLocalRoute);
    setSelectedBusToEdit(busWithLocalRoute);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedBusToEdit(null);
  };

  const handleDeleteDriver = async (driverId, driverName) => {
    const driver = drivers.find(d => d._id === driverId);
    if (driver && driver.assignedBus) {
      if (!window.confirm(`Driver "${driverName}" is currently assigned to a bus. Do you want to unassign and delete them?`)) {
        return;
      }
      try {
        await unassignDriverFromBus(driverId, owner.id, token);
      } catch (error) {
        toast.error('Failed to unassign driver before deletion');
        return;
      }
    }

    if (!window.confirm(`Are you sure you want to delete driver "${driverName}"?`)) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [`delete-driver-${driverId}`]: true }));
    try {
      const response = await deleteDriver(driverId, token);
      if (response.success) {
        toast.success('Driver deleted successfully');
        setDrivers(prev => prev.filter(d => d._id !== driverId));
        setStats(prev => ({
          ...prev,
          totalDrivers: prev.totalDrivers - 1,
          assignedDrivers: driver?.assignedBus ? prev.assignedDrivers - 1 : prev.assignedDrivers
        }));
        await fetchAllData();
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.message || 'Failed to delete driver');
      }
    } catch (error) {
      console.error('Error deleting driver:', error);
      toast.error(error.response?.data?.message || 'Failed to delete driver');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete-driver-${driverId}`]: false }));
    }
  };

  const handleDeleteConductor = async (conductorId, conductorName) => {
    const conductor = conductors.find(c => c._id === conductorId);
    if (conductor && conductor.assignedBus) {
      if (!window.confirm(`Conductor "${conductorName}" is currently assigned to a bus. Do you want to unassign and delete them?`)) {
        return;
      }
      try {
        await unassignConductorFromBus(conductorId, owner.id, token);
      } catch (error) {
        toast.error('Failed to unassign conductor before deletion');
        return;
      }
    }

    if (!window.confirm(`Are you sure you want to delete conductor "${conductorName}"?`)) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [`delete-conductor-${conductorId}`]: true }));
    try {
      const response = await deleteConductor(conductorId, token);
      if (response.success) {
        toast.success('Conductor deleted successfully');
        setConductors(prev => prev.filter(c => c._id !== conductorId));
        setStats(prev => ({
          ...prev,
          totalConductors: prev.totalConductors - 1,
          assignedConductors: conductor?.assignedBus ? prev.assignedConductors - 1 : prev.assignedConductors
        }));
        await fetchAllData();
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.message || 'Failed to delete conductor');
      }
    } catch (error) {
      console.error('Error deleting conductor:', error);
      toast.error(error.response?.data?.message || 'Failed to delete conductor');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete-conductor-${conductorId}`]: false }));
    }
  };

  useEffect(() => {
    if (owner && owner.id) {
      fetchAllData();
    }
  }, [owner?.id]);

  useEffect(() => {
    if (buses && drivers && conductors) {
      setStats({
        totalBuses: buses.length,
        activeBuses: buses.filter(b => b.isActive).length,
        totalDrivers: drivers.length,
        assignedDrivers: drivers.filter(d => d.assignedBus).length,
        totalConductors: conductors.length,
        assignedConductors: conductors.filter(c => c.assignedBus).length
      });
    }
  }, [buses, drivers, conductors]);

  useEffect(() => {
    console.log('Buses prop changed in BusManagementModal:', buses);
  }, [buses]);

  useEffect(() => {
    if (buses && drivers && conductors) {
      setStats({
        totalBuses: buses.length,
        activeBuses: buses.filter(b => b.isActive).length,
        totalDrivers: drivers.length,
        assignedDrivers: drivers.filter(d => d.assignedBus).length,
        totalConductors: conductors.length,
        assignedConductors: conductors.filter(c => c.assignedBus).length
      });
    }
  }, [buses, drivers, conductors]);

  const fetchAllData = async () => {
    if (!owner || !owner.id) return;
    
    setLoading(true);
    try {
      const [driversRes, conductorsRes] = await Promise.all([
        getDriversByBusOwner(owner.id, token, { limit: 100 }),
        getConductorsByBusOwner(owner.id, token, { limit: 100 })
      ]);

      if (driversRes.success) setDrivers(driversRes.drivers || []);
      if (conductorsRes.success) setConductors(conductorsRes.conductors || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDriver = async (driverId, busId) => {
    setActionLoading(prev => ({ ...prev, [`driver-${driverId}`]: true }));
    try {
      const response = await assignDriverToBus(driverId, busId, owner.id, token);
      if (response.success) {
        toast.success('Driver assigned successfully');
        await fetchAllData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign driver');
    } finally {
      setActionLoading(prev => ({ ...prev, [`driver-${driverId}`]: false }));
    }
  };

  const handleUnassignDriver = async (driverId) => {
    setActionLoading(prev => ({ ...prev, [`unassign-driver-${driverId}`]: true }));
    try {
      const response = await unassignDriverFromBus(driverId, owner.id, token);
      if (response.success) {
        toast.success('Driver unassigned successfully');
        await fetchAllData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unassign driver');
    } finally {
      setActionLoading(prev => ({ ...prev, [`unassign-driver-${driverId}`]: false }));
    }
  };

  const handleAssignConductor = async (conductorId, busId) => {
    setActionLoading(prev => ({ ...prev, [`conductor-${conductorId}`]: true }));
    try {
      const response = await assignConductorToBus(conductorId, busId, owner.id, token);
      if (response.success) {
        toast.success('Conductor assigned successfully');
        await fetchAllData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign conductor');
    } finally {
      setActionLoading(prev => ({ ...prev, [`conductor-${conductorId}`]: false }));
    }
  };

  const handleUnassignConductor = async (conductorId) => {
    setActionLoading(prev => ({ ...prev, [`unassign-conductor-${conductorId}`]: true }));
    try {
      const response = await unassignConductorFromBus(conductorId, owner.id, token);
      if (response.success) {
        toast.success('Conductor unassigned successfully');
        await fetchAllData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unassign conductor');
    } finally {
      setActionLoading(prev => ({ ...prev, [`unassign-conductor-${conductorId}`]: false }));
    }
  };

  const getAssignedDriver = (busId) => {
    return drivers.find(d => d.assignedBus?._id === busId);
  };

  const getAssignedConductor = (busId) => {
    return conductors.find(c => c.assignedBus?._id === busId);
  };

  const getAvailableDrivers = (busId) => {
    return drivers.filter(d => 
      !d.assignedBus || d.assignedBus._id === busId
    ).filter(d => 
      d.name?.toLowerCase().includes(searchDriver.toLowerCase()) ||
      d.mobileNumber?.includes(searchDriver) ||
      (d.licenseNumber && d.licenseNumber.toLowerCase().includes(searchDriver.toLowerCase()))
    );
  };

  const getAvailableConductors = (busId) => {
    return conductors.filter(c => 
      !c.assignedBus || c.assignedBus._id === busId
    ).filter(c => 
      c.name?.toLowerCase().includes(searchConductor.toLowerCase()) ||
      c.mobileNumber?.includes(searchConductor)
    );
  };

  const FmStatCard = ({ icon, label, value, subValue, color }) => (
    <div className="fm-stat-card">
      <div className="fm-stat-icon-wrapper" style={{ 
        background: `linear-gradient(135deg, ${color}20, ${color}05)`,
        color: color 
      }}>
        {icon}
      </div>
      <div className="fm-stat-details">
        <span className="fm-stat-label">{label}</span>
        <span className="fm-stat-value">
          {value}
          {subValue && <span className="fm-stat-trend">{subValue}</span>}
        </span>
      </div>
    </div>
  );

  if (!buses || buses.length === 0) {
    return (
      <div className="fm-modal-overlay" onClick={onClose}>
        <div className="fm-modal-content fm-glass-effect" onClick={(e) => e.stopPropagation()}>
          <div className="fm-modern-header">
            <div className="fm-header-content">
              <div className="fm-header-title">
                <div className="fm-header-icon-wrapper">
                  <span>🚌</span>
                </div>
                <div className="fm-header-text">
                  <h1>Fleet Management</h1>
                  <p>
                    <span>{owner?.name || 'Owner'}</span>
                    <span className="fm-company-badge">{owner?.company || 'No Company'}</span>
                  </p>
                </div>
              </div>
              <button className="fm-close-button" onClick={onClose}>×</button>
            </div>
          </div>
          
          <div className="fm-empty-state">
            <div className="fm-empty-icon">🚫</div>
            <h3>No Buses Available</h3>
            <p>This owner hasn't registered any buses yet</p>
            <button className="fm-empty-action-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fm-modal-overlay" onClick={onClose}>
        <div className="fm-modal-content fm-glass-effect" onClick={(e) => e.stopPropagation()}>

          <div className="fm-modern-header">
            <div className="fm-header-content">
              <div className="fm-header-title">
                <div className="fm-header-icon-wrapper">
                  <span>🚍</span>
                </div>
                <div className="fm-header-text">
                  <h1>Fleet Management</h1>
                  <p>
                    <span>{owner?.name || 'Owner'}</span>
                    <span className="fm-company-badge">{owner?.company || 'Individual'}</span>
                  </p>
                </div>
              </div>
              <button className="fm-close-button" onClick={onClose}>×</button>
            </div>
          </div>


          <div className="fm-stats-grid">
            <FmStatCard 
              icon="🚌" 
              label="Total Buses" 
              value={stats.totalBuses}
              color="#6366f1"
            />
            <FmStatCard 
              icon="✅" 
              label="Active Buses" 
              value={stats.activeBuses}
              subValue={`${Math.round((stats.activeBuses/stats.totalBuses)*100)}%`}
              color="#22c55e"
            />
            <FmStatCard 
              icon="👤" 
              label="Drivers" 
              value={`${stats.assignedDrivers}/${stats.totalDrivers}`}
              subValue="assigned"
              color="#f59e0b"
            />
            <FmStatCard 
              icon="👥" 
              label="Conductors" 
              value={`${stats.assignedConductors}/${stats.totalConductors}`}
              subValue="assigned"
              color="#ec4899"
            />
          </div>


          <div className="fm-tabs-container">
            <div className="fm-tabs">
              <button 
                className={`fm-tab ${activeTab === 'buses' ? 'fm-active' : ''}`}
                onClick={() => setActiveTab('buses')}
              >
                <span className="fm-tab-icon">🚌</span>
                Buses
                <span className="fm-tab-badge">{stats.totalBuses}</span>
              </button>
              <button 
                className={`fm-tab ${activeTab === 'drivers' ? 'fm-active' : ''}`}
                onClick={() => setActiveTab('drivers')}
              >
                <span className="fm-tab-icon">👤</span>
                Drivers
                <span className="fm-tab-badge">{stats.totalDrivers}</span>
              </button>
              <button 
                className={`fm-tab ${activeTab === 'conductors' ? 'fm-active' : ''}`}
                onClick={() => setActiveTab('conductors')}
              >
                <span className="fm-tab-icon">👥</span>
                Conductors
                <span className="fm-tab-badge">{stats.totalConductors}</span>
              </button>
            </div>

            {activeTab !== 'buses' && (
              <div className="fm-tab-actions">
                {activeTab === 'drivers' && (
                  <button 
                    className="fm-add-staff-btn"
                    onClick={() => setShowAddDriverModal(true)}
                  >
                    <span>+</span> Add Driver
                  </button>
                )}
                {activeTab === 'conductors' && (
                  <button 
                    className="fm-add-staff-btn"
                    onClick={() => setShowAddConductorModal(true)}
                  >
                    <span>+</span> Add Conductor
                  </button>
                )}
              </div>
            )}

            {activeTab === 'buses' && (
              <div className="fm-view-toggle">
                <button 
                  className={`fm-view-btn ${viewMode === 'grid' ? 'fm-active' : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  <span>🔲</span> Grid
                </button>
                <button 
                  className={`fm-view-btn ${viewMode === 'list' ? 'fm-active' : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  <span>📋</span> List
                </button>
              </div>
            )}
          </div>


          <div className="fm-modern-body">
            {loading ? (
              <div className="fm-loading-container">
                <div className="fm-modern-spinner"></div>
                <p>Loading fleet data...</p>
              </div>
            ) : (
              <>

                {activeTab === 'buses' && (
                  <div className={viewMode === 'grid' ? 'fm-buses-grid' : 'fm-buses-list'}>
                    {buses.map(bus => {
                      const assignedDriver = getAssignedDriver(bus._id);
                      const assignedConductor = getAssignedConductor(bus._id);
                      const availableDrivers = getAvailableDrivers(bus._id);
                      const availableConductors = getAvailableConductors(bus._id);
                      
                      return viewMode === 'grid' ? (
                        <div 
                          key={bus._id} 
                          className={`fm-bus-card ${selectedBus?._id === bus._id ? 'fm-selected' : ''}`}
                          onClick={() => setSelectedBus(selectedBus?._id === bus._id ? null : bus)}
                        >
                          <div className="fm-bus-card-header">
                            <div className="fm-bus-title-section">
                              <div className="fm-bus-name">
                                <h3>{bus.busName || 'Unnamed Bus'}</h3>
                                {bus.busType && (
                                  <span className="fm-bus-type-badge">{bus.busType}</span>
                                )}
                                 {bus.ticketBookingType && (
        <span 
          className="fm-ticket-type-badge"
          style={{
            backgroundColor: getTicketTypeBadge(bus).color,
            color: 'white',
            padding: '2px 10px',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: '600',
            marginLeft: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>{getTicketTypeBadge(bus).icon}</span>
          {getTicketTypeBadge(bus).label}
        </span>
      )}
                              </div>
                              <span className="fm-bus-number-tag">{bus.busNumber || 'N/A'}</span>
                            </div>
                            <div className="fm-status-toggle">
                              <span className={`fm-status-badge ${bus.isActive ? 'fm-active' : 'fm-inactive'}`}>
                                <span>{bus.isActive ? '●' : '○'}</span>
                                {bus.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>

                          <div className="fm-bus-card-body">

                            <div className="fm-bus-specs">
                              <div className="fm-spec-item">
                                <span className="fm-spec-icon">🚍</span>
                                <span className="fm-spec-value">{bus.busCategory || 'Standard'}</span>
                                <span className="fm-spec-label">Category</span>
                              </div>
                              <div className="fm-spec-item">
                                <span className="fm-spec-icon">🪑</span>
                                <span className="fm-spec-value">{bus.totalSeats || 0}</span>
                                <span className="fm-spec-label">Seats</span>
                              </div>
                              {bus.availableSeats && (
                                <div className="fm-spec-item">
                                  <span className="fm-spec-icon">💺</span>
                                  <span className="fm-spec-value">{bus.availableSeats}</span>
                                  <span className="fm-spec-label">Available</span>
                                </div>
                              )}
                            </div>


                            <div className="fm-staff-section">

                              <div className="fm-staff-row">
                                <div className="fm-staff-role-icon">👤</div>
                                <div className="fm-staff-info-compact">
                                  {assignedDriver ? (
                                    <>
                                      <span className="fm-staff-name-tag">{assignedDriver.name}</span>
                                      <span className="fm-staff-contact-tag">
                                        📞 {assignedDriver.mobileNumber}
                                      </span>
                                      <button 
                                        className="fm-unassign-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUnassignDriver(assignedDriver._id);
                                        }}
                                        disabled={actionLoading[`unassign-driver-${assignedDriver._id}`]}
                                      >
                                        {actionLoading[`unassign-driver-${assignedDriver._id}`] ? '...' : '✕ Unassign'}
                                      </button>
                                    </>
                                  ) : (
                                    <div className="fm-assign-dropdown">
                                      <select 
                                        className="fm-assign-select-modern"
                                        onChange={(e) => {
                                          if (e.target.value) {
                                            handleAssignDriver(e.target.value, bus._id);
                                          }
                                        }}
                                        value=""
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <option value="">Assign Driver</option>
                                        {availableDrivers.map(driver => (
                                          <option key={driver._id} value={driver._id}>
                                            {driver.name} - {driver.mobileNumber}
                                            {driver.experience ? ` (${driver.experience} yrs)` : ''}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>
                              </div>


                              <div className="fm-staff-row">
                                <div className="fm-staff-role-icon">👥</div>
                                <div className="fm-staff-info-compact">
                                  {assignedConductor ? (
                                    <>
                                      <span className="fm-staff-name-tag">{assignedConductor.name}</span>
                                      <span className="fm-staff-contact-tag">
                                        📞 {assignedConductor.mobileNumber}
                                      </span>
                                      <button 
                                        className="fm-unassign-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUnassignConductor(assignedConductor._id);
                                        }}
                                        disabled={actionLoading[`unassign-conductor-${assignedConductor._id}`]}
                                      >
                                        {actionLoading[`unassign-conductor-${assignedConductor._id}`] ? '...' : '✕ Unassign'}
                                      </button>
                                    </>
                                  ) : (
                                    <div className="fm-assign-dropdown">
                                      <select 
                                        className="fm-assign-select-modern"
                                        onChange={(e) => {
                                          if (e.target.value) {
                                            handleAssignConductor(e.target.value, bus._id);
                                          }
                                        }}
                                        value=""
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <option value="">Assign Conductor</option>
                                        {availableConductors.map(conductor => (
                                          <option key={conductor._id} value={conductor._id}>
                                            {conductor.name} - {conductor.mobileNumber}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>


                            {bus.amenities && bus.amenities.length > 0 && (
                              <div className="fm-amenities-section">
                                {bus.amenities.slice(0, 5).map((amenity, i) => (
                                  <span key={i} className="fm-amenity-chip">{amenity}</span>
                                ))}
                                {bus.amenities.length > 5 && (
                                  <span className="fm-amenity-chip fm-more">+{bus.amenities.length - 5}</span>
                                )}
                              </div>
                            )}


                            <div className="fm-bus-actions">
                              <button 
                                className="fm-edit-bus-btn"
                                onClick={(e) => handleEditBus(bus, e)}
                              >
                                ✏️ Edit Bus
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div 
                          key={bus._id} 
                          className={`fm-bus-list-item ${selectedBus?._id === bus._id ? 'fm-selected' : ''}`}
                          onClick={() => setSelectedBus(selectedBus?._id === bus._id ? null : bus)}
                        >
                          <div className="fm-bus-list-content">
                            <div className="fm-bus-list-left">
                              <div className="fm-bus-list-icon">🚌</div>
                              <div className="fm-bus-list-info">
                                <span className="fm-bus-list-name">{bus.busName || 'Unnamed'}</span>
                                  {bus.ticketBookingType && (
          <span 
            className="fm-ticket-type-badge-list"
            style={{
              backgroundColor: getTicketTypeBadge(bus).color,
              color: 'white',
              padding: '1px 8px',
              borderRadius: '10px',
              fontSize: '9px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px'
            }}
          >
            <span>{getTicketTypeBadge(bus).icon}</span>
            {getTicketTypeBadge(bus).label}
          </span>
        )}
                                <span className="fm-bus-list-number">{bus.busNumber || 'N/A'}</span>
                                <span className={`fm-bus-list-status ${bus.isActive ? 'fm-active' : 'fm-inactive'}`}>
                                  {bus.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>

                            <div className="fm-bus-list-staff">
                              <div className="fm-staff-pill">
                                <span className="fm-label">Driver:</span>
                                <span className="fm-name">
                                  {getAssignedDriver(bus._id)?.name || 
                                    <span className="fm-unassigned">Not assigned</span>}
                                </span>
                              </div>
                              <div className="fm-staff-pill">
                                <span className="fm-label">Conductor:</span>
                                <span className="fm-name">
                                  {getAssignedConductor(bus._id)?.name || 
                                    <span className="fm-unassigned">Not assigned</span>}
                                </span>
                              </div>
                            </div>


                            <div className="fm-bus-list-actions">
                              <button 
                                className="fm-edit-bus-list-btn"
                                onClick={(e) => handleEditBus(bus, e)}
                              >
                                ✏️ Edit
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}


                {activeTab === 'drivers' && (
                  <div className="fm-staff-container">
                    <div className="fm-staff-header-actions">
                      <div className="fm-search-wrapper">
                        <span className="fm-search-icon">🔍</span>
                        <input
                          type="text"
                          placeholder="Search drivers by name, phone, license..."
                          className="fm-staff-search-modern"
                          value={searchDriver}
                          onChange={(e) => setSearchDriver(e.target.value)}
                        />
                      </div>
                      <button className="fm-filter-btn">
                        <span>⚙️</span> Filter
                      </button>
                    </div>

                    <div className="fm-staff-grid-modern">
                      {drivers.length > 0 ? (
                        drivers
                          .filter(d => 
                            d.name?.toLowerCase().includes(searchDriver.toLowerCase()) ||
                            d.mobileNumber?.includes(searchDriver) ||
                            (d.licenseNumber && d.licenseNumber.toLowerCase().includes(searchDriver.toLowerCase()))
                          )
                          .map(driver => (
                            <div key={driver._id} className="fm-staff-card-modern">
                              <div className="fm-staff-avatar-modern">
                                {driver.profileImage ? (
                                  <img src={driver.profileImage} alt={driver.name} />
                                ) : (
                                  <div className="fm-avatar-placeholder-modern">
                                    {driver.name?.charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              
                              <div className="fm-staff-info-modern">
                                <h4>{driver.name}</h4>
                                
                                <div className="fm-staff-badges">
                                  <span className="fm-staff-badge">📞 {driver.mobileNumber}</span>
                                  {driver.experience && (
                                    <span className="fm-staff-badge fm-experience">
                                      ⏱️ {driver.experience} yrs
                                    </span>
                                  )}
                                </div>

                                <div className="fm-staff-details">
                                  {driver.licenseNumber && (
                                    <div className="fm-detail-item">
                                      <span className="fm-detail-icon">🪪</span>
                                      <span>{driver.licenseNumber}</span>
                                    </div>
                                  )}
                                  {driver.email && (
                                    <div className="fm-detail-item">
                                      <span className="fm-detail-icon">📧</span>
                                      <span>{driver.email}</span>
                                    </div>
                                  )}
                                </div>

                                <div className={`fm-assignment-card ${driver.assignedBus ? 'fm-assigned' : 'fm-available'}`}>
                                  <div className={`fm-assignment-header ${driver.assignedBus ? 'fm-assigned' : 'fm-available'}`}>
                                    <span>{driver.assignedBus ? '🚍' : '✅'}</span>
                                    {driver.assignedBus ? 'Currently Assigned' : 'Available for Assignment'}
                                  </div>
                                  
                                  {driver.assignedBus ? (
                                    <>
                                      <div className="fm-bus-info-compact">
                                        <div className="fm-bus-icon-small">🚌</div>
                                        <div className="fm-bus-details-small">
                                          <span className="fm-bus-name-small">{driver.assignedBus.busName}</span>
                                          <span className="fm-bus-number-small">{driver.assignedBus.busNumber}</span>
                                        </div>
                                      </div>
                                      <div className="fm-staff-actions-row">
                                        <button 
                                          className="fm-unassign-btn-modern"
                                          onClick={() => handleUnassignDriver(driver._id)}
                                          disabled={actionLoading[`unassign-driver-${driver._id}`]}
                                        >
                                          {actionLoading[`unassign-driver-${driver._id}`] ? '...' : '🔄 Unassign'}
                                        </button>
                                        <button 
                                          className="fm-delete-btn-modern fm-delete-driver"
                                          onClick={() => handleDeleteDriver(driver._id, driver.name)}
                                          disabled={actionLoading[`delete-driver-${driver._id}`]}
                                        >
                                          {actionLoading[`delete-driver-${driver._id}`] ? '...' : '🗑️ Delete'}
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="fm-available-badge-modern">
                                        <span>✨ Ready to assign</span>
                                      </div>
                                      <button 
                                        className="fm-delete-btn-modern fm-delete-driver"
                                        onClick={() => handleDeleteDriver(driver._id, driver.name)}
                                        disabled={actionLoading[`delete-driver-${driver._id}`]}
                                      >
                                        {actionLoading[`delete-driver-${driver._id}`] ? '...' : '🗑️ Delete'}
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                      ) : (
                        <div className="fm-empty-state">
                          <div className="fm-empty-icon">👤</div>
                          <h3>No Drivers Found</h3>
                          <p>No drivers are registered for this owner yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}


                {activeTab === 'conductors' && (
                  <div className="fm-staff-container">
                    <div className="fm-staff-header-actions">
                      <div className="fm-search-wrapper">
                        <span className="fm-search-icon">🔍</span>
                        <input
                          type="text"
                          placeholder="Search conductors by name, phone..."
                          className="fm-staff-search-modern"
                          value={searchConductor}
                          onChange={(e) => setSearchConductor(e.target.value)}
                        />
                      </div>
                      <button className="fm-filter-btn">
                        <span>⚙️</span> Filter
                      </button>
                    </div>

                    <div className="fm-staff-grid-modern">
                      {conductors.length > 0 ? (
                        conductors
                          .filter(c => 
                            c.name?.toLowerCase().includes(searchConductor.toLowerCase()) ||
                            c.mobileNumber?.includes(searchConductor)
                          )
                          .map(conductor => (
                            <div key={conductor._id} className="fm-staff-card-modern">
                              <div className="fm-staff-avatar-modern">
                                {conductor.profileImage ? (
                                  <img src={conductor.profileImage} alt={conductor.name} />
                                ) : (
                                  <div className="fm-avatar-placeholder-modern">
                                    {conductor.name?.charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              
                              <div className="fm-staff-info-modern">
                                <h4>{conductor.name}</h4>
                                
                                <div className="fm-staff-badges">
                                  <span className="fm-staff-badge">📞 {conductor.mobileNumber}</span>
                                </div>

                                <div className="fm-staff-details">
                                  {conductor.email && (
                                    <div className="fm-detail-item">
                                      <span className="fm-detail-icon">📧</span>
                                      <span>{conductor.email}</span>
                                    </div>
                                  )}
                                  {conductor.address && (
                                    <div className="fm-detail-item">
                                      <span className="fm-detail-icon">📍</span>
                                      <span>{conductor.address}</span>
                                    </div>
                                  )}
                                </div>

                                <div className={`fm-assignment-card ${conductor.assignedBus ? 'fm-assigned' : 'fm-available'}`}>
                                  <div className={`fm-assignment-header ${conductor.assignedBus ? 'fm-assigned' : 'fm-available'}`}>
                                    <span>{conductor.assignedBus ? '🚍' : '✅'}</span>
                                    {conductor.assignedBus ? 'Currently Assigned' : 'Available for Assignment'}
                                  </div>
                                  
                                  {conductor.assignedBus ? (
                                    <>
                                      <div className="fm-bus-info-compact">
                                        <div className="fm-bus-icon-small">🚌</div>
                                        <div className="fm-bus-details-small">
                                          <span className="fm-bus-name-small">{conductor.assignedBus.busName}</span>
                                          <span className="fm-bus-number-small">{conductor.assignedBus.busNumber}</span>
                                        </div>
                                      </div>
                                      <div className="fm-staff-actions-row">
                                        <button 
                                          className="fm-unassign-btn-modern"
                                          onClick={() => handleUnassignConductor(conductor._id)}
                                          disabled={actionLoading[`unassign-conductor-${conductor._id}`]}
                                        >
                                          {actionLoading[`unassign-conductor-${conductor._id}`] ? '...' : '🔄 Unassign'}
                                        </button>
                                        <button 
                                          className="fm-delete-btn-modern fm-delete-conductor"
                                          onClick={() => handleDeleteConductor(conductor._id, conductor.name)}
                                          disabled={actionLoading[`delete-conductor-${conductor._id}`]}
                                        >
                                          {actionLoading[`delete-conductor-${conductor._id}`] ? '...' : '🗑️ Delete'}
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="fm-available-badge-modern">
                                        <span>✨ Ready to assign</span>
                                      </div>
                                      <button 
                                        className="fm-delete-btn-modern fm-delete-conductor"
                                        onClick={() => handleDeleteConductor(conductor._id, conductor.name)}
                                        disabled={actionLoading[`delete-conductor-${conductor._id}`]}
                                      >
                                        {actionLoading[`delete-conductor-${conductor._id}`] ? '...' : '🗑️ Delete'}
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                      ) : (
                        <div className="fm-empty-state">
                          <div className="fm-empty-icon">👥</div>
                          <h3>No Conductors Found</h3>
                          <p>No conductors are registered for this owner yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>


      {showEditModal && selectedBusToEdit && (
        <EditBusModal
          bus={selectedBusToEdit}
          owner={owner}
          onClose={closeEditModal}
          onUpdate={async () => {
            await fetchAllData();
            if (onUpdate) {
              await onUpdate();
            }
            closeEditModal();
          }}
        />
      )}


      {showAddDriverModal && (
        <AddDriverModal
          busOwnerId={owner.id}
          token={token}
          onClose={() => setShowAddDriverModal(false)}
          onSuccess={handleAddDriverSuccess}
        />
      )}


      {showAddConductorModal && (
        <AddConductorModal
          busOwnerId={owner.id}
          token={token}
          onClose={() => setShowAddConductorModal(false)}
          onSuccess={handleAddConductorSuccess}
        />
      )}
    </>
  );
};

export default BusManagementModal;